import { AffordabilityBand, AffordabilityReading } from './types'

const COMFORTABLE_THRESHOLD = 0.3
const TIGHT_THRESHOLD = 0.4
const STRETCH_THRESHOLD = 0.5

/**
 * Picks the affordability band a given rent-to-income ratio falls into.
 * Reflects the rough rule-of-thumb: under 30% of take-home is the
 * "comfortable" budget, 30-40% is tight, 40-50% is a stretch, anything
 * above 50% is an overspend.
 *
 * @param {number} ratio - the rent-to-income ratio (rent / take-home)
 * @returns {AffordabilityBand} the band the ratio falls into
 */
const resolveBand = (ratio: number): AffordabilityBand => {
  if (ratio <= COMFORTABLE_THRESHOLD) {
    return 'comfortable'
  }

  if (ratio <= TIGHT_THRESHOLD) {
    return 'tight'
  }

  if (ratio <= STRETCH_THRESHOLD) {
    return 'stretch'
  }

  return 'overspend'
}

/**
 * Picks a renter-friendly band label.
 *
 * @param {AffordabilityBand} band - the resolved band
 * @returns {string} the band label
 */
const labelForBand = (band: AffordabilityBand): string => {
  if (band === 'comfortable') {
    return 'Comfortably under 30%'
  }

  if (band === 'tight') {
    return 'Tight — over 30%'
  }

  if (band === 'stretch') {
    return 'Stretch — 40-50%'
  }

  return 'Overspend — over 50%'
}

/**
 * Builds the supportive hint line under the band label.
 *
 * @param {AffordabilityBand} band - the resolved band
 * @returns {string} the hint copy
 */
const hintForBand = (band: AffordabilityBand): string => {
  if (band === 'comfortable') {
    return 'Plenty of room for utilities, groceries, and savings.'
  }

  if (band === 'tight') {
    return 'Workable but watch the discretionary spend.'
  }

  if (band === 'stretch') {
    return "You'll need a careful budget for everything else."
  }

  return 'This place will dominate your monthly budget.'
}

/**
 * Computes the affordability reading for a listing given the renter's
 * stored monthly take-home income. Returns a "no income set" reading
 * when income is null.
 *
 * @param {number | null} monthlyRent - the listing's monthly rent
 * @param {number | null} monthlyTakeHome - the renter's stored take-home
 * @returns {AffordabilityReading} the resolved reading
 */
const buildAffordabilityReading = (
  monthlyRent: number | null,
  monthlyTakeHome: number | null,
): AffordabilityReading => {
  if (
    monthlyRent === null ||
    monthlyTakeHome === null ||
    monthlyTakeHome <= 0
  ) {
    return {
      ratio: 0,
      percent: 0,
      band: 'comfortable',
      label: 'No income set',
      hint: 'Add your take-home in the profile to see how this fits.',
      hasIncome: false,
    }
  }

  const ratio = monthlyRent / monthlyTakeHome
  const percent = Math.round(ratio * 100)
  const band = resolveBand(ratio)

  return {
    ratio,
    percent,
    band,
    label: labelForBand(band),
    hint: hintForBand(band),
    hasIncome: true,
  }
}

export { buildAffordabilityReading }
